import React, { useRef, useState, useEffect } from 'react';
import './FAQAccordian.css';
import faqQuestions from './faqdata';

export function FAQItem({ question, answer }) {
    const [open, setOpen] = useState(false);
    const el = useRef(null);

    useEffect(() => {
        const node = el.current;
        if (!node) return;

        if (open) {
            node.style.maxHeight = node.scrollHeight + 'px';
            node.style.padding = '16px 0';
            const onEnd = () => {
                node.style.maxHeight = 'none';
                node.removeEventListener('transitionend', onEnd);
            };
            node.addEventListener('transitionend', onEnd);
        } else {
            // ensure measurable height before collapsing
            if (node.style.maxHeight === 'none') node.style.maxHeight = node.scrollHeight + 'px';
            requestAnimationFrame(() => {
                node.style.maxHeight = '0';
                node.style.padding = '0';
            });
        }
    }, [open]);

    return (
        <div className="googlelp-faq-item">
            <button className="googlelp-faq-question" onClick={() => setOpen((s) => !s)} aria-expanded={open}>
                {question}
            </button>
            <div ref={el} className="googlelp-faq-answer" aria-hidden={!open}>
                <div className="googlelp-faq-answer-inner">{answer}</div>
            </div>
        </div>
    );
}

export function FAQAccordian() {
    const [openIndex, setOpenIndex] = useState(null);
    const btnRefs = useRef([]);
    const answerRefs = useRef([]);

    // Toggle open/close
    const toggle = (i) => setOpenIndex((prev) => (prev === i ? null : i));

    // Keyboard navigation: arrows, Home, End, Enter/Space
    const onKeyDown = (e, idx) => {
        const key = e.key;
        const last = faqQuestions.length - 1;
        if (key === 'ArrowDown') {
            e.preventDefault();
            const next = idx === last ? 0 : idx + 1;
            btnRefs.current[next]?.focus();
        } else if (key === 'ArrowUp') {
            e.preventDefault();
            const prev = idx === 0 ? last : idx - 1;
            btnRefs.current[prev]?.focus();
        } else if (key === 'Enter' || key === ' ') {
            e.preventDefault();
            toggle(idx);
        }
    };

    // Update maxHeight for animated reveal when openIndex changes
    useEffect(() => {
        faqQuestions.forEach((_, i) => {
            const node = answerRefs.current[i];
            if (node) {
                if (openIndex === i) {
                    const scroll = node.scrollHeight;
                    node.style.maxHeight = scroll + 'px';
                } else {
                    node.style.maxHeight = '0px';
                }
            }
        });
    }, [openIndex, faqQuestions]);

    return (
        <section className="googlelp-faq googlelp-container py-5" id="googlelp-faq">
            <div className="row">
                <div className="col-12">
                    <p className="text-center mcl-font-greycliff mb-0 googlelp-faq-header font-bold pb-3 pb-md-5">
                        Still deciding? Here are questions we hear most
                    </p>
                    <dl className="googlelp-faq-list">
                        {faqQuestions.map((item, i) => (
                            <div className="googlelp-faq-item" key={i}>
                                <dt className="pb-3">
                                    <button
                                        ref={(el) => (btnRefs.current[i] = el)}
                                        className="googlelp-faq-question d-flex align-items-center justify-content-between w-100 pt-3"
                                        aria-expanded={openIndex === i}
                                        aria-controls={`googlelp-faq-answer-${i}`}
                                        id={`googlelp-faq-question-${i}`}
                                        onClick={() => toggle(i)}
                                        onKeyDown={(e) => onKeyDown(e, i)}
                                    >
                                        <span>{item.q}</span>
                                        <span className={`faq-arrow ${openIndex === i ? 'open' : ''}`} aria-hidden>
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M6 9l6 6 6-6"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    </button>
                                </dt>
                                <dd
                                    ref={(el) => (answerRefs.current[i] = el)}
                                    id={`googlelp-faq-answer-${i}`}
                                    className={`mcl-font-proxima googlelp-faq-answer ${openIndex === i ? 'open' : ''}`}
                                    role="region"
                                    aria-labelledby={`googlelp-faq-question-${i}`}
                                    aria-hidden={openIndex === i ? 'false' : 'true'}
                                >
                                    <div className="googlelp-faq-answer-inner">
                                        <p className="mb-0">{item.a}</p>
                                    </div>
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </section>
    );
}

export default FAQAccordian;
